import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Stack, Chip, Skeleton, Collapse, Button } from '@mui/material';
import {
  ShoppingCart, PrecisionManufacturing, FactCheck, PointOfSale, Payments,
  AutoStories, East, UnfoldMore, UnfoldLess,
} from '@mui/icons-material';
import { RouterLink } from '../../utils/linkNav';
import { useControlRoomColors } from '../common/controlRoom';
import { financeWorkspaceService } from '../../services/financeWorkspaceService';
import { formatIndianNumber } from '../../utils/currency';

/**
 * Demo Story Mode (§38) — "Today's Manufacturing Story". Assembles the real
 * transaction spine (Buy → Make → Check → Sell → Collect) into a narrative
 * ribbon. Every beat is a real record from GET /demo-story; clicking opens it.
 */

const KIND: Record<string, { icon: React.ReactNode; label: string; tone: 'accent' | 'warn' | 'good' | 'cyan' | 'purple' }> = {
  buy: { icon: <ShoppingCart />, label: 'Procure', tone: 'purple' },
  make: { icon: <PrecisionManufacturing />, label: 'Produce', tone: 'accent' },
  quality: { icon: <FactCheck />, label: 'Quality', tone: 'cyan' },
  sell: { icon: <PointOfSale />, label: 'Sell', tone: 'good' },
  collect: { icon: <Payments />, label: 'Collect', tone: 'warn' },
};

const DemoStory: React.FC = () => {
  const navigate = useNavigate();
  const C = useControlRoomColors();
  const [data, setData] = useState<any>(null);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    let alive = true;
    financeWorkspaceService.demoStory()
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData({ chapters: [] }); });
    return () => { alive = false; };
  }, []);

  const toneColor = (t: string) => (C as any)[t] || C.accent;
  const chapters = data?.chapters || [];

  return (
    <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: open ? 1.5 : 0 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <AutoStories sx={{ color: C.accent }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 15, color: C.text }}>Today's Manufacturing Story</Typography>
            <Typography sx={{ fontSize: 12, color: C.sub }}>
              {data ? `${data.date} · ${data.headline}` : 'Assembling the day…'}
            </Typography>
          </Box>
        </Stack>
        <Button size="small" onClick={() => setOpen((o) => !o)} startIcon={open ? <UnfoldLess /> : <UnfoldMore />}
          sx={{ color: C.sub, textTransform: 'none' }}>{open ? 'Collapse' : 'Expand'}</Button>
      </Stack>

      <Collapse in={open}>
        {!data ? (
          <Stack direction="row" spacing={1}>
            {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rounded" height={96} sx={{ flex: 1, bgcolor: C.panel2 }} />)}
          </Stack>
        ) : chapters.length === 0 ? (
          <Typography sx={{ color: C.sub, fontSize: 13, py: 2, textAlign: 'center' }}>No recent activity to narrate yet.</Typography>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 0, overflowX: 'auto', pb: 1 }}>
            {chapters.map((c: any, idx: number) => {
              const meta = KIND[c.kind] || KIND.make;
              const col = toneColor(meta.tone);
              return (
                <React.Fragment key={idx}>
                  <Box
                    {...((c.route ? { component: RouterLink, to: c.route } : {}) as any)}
                    sx={{
                      flex: '1 0 200px', minWidth: 200, cursor: c.route ? 'pointer' : 'default',
                      textDecoration: 'none', color: 'inherit',
                      bgcolor: C.panel2, border: `1px solid ${C.line}`, borderTop: `3px solid ${col}`,
                      borderRadius: 2, p: 1.5, transition: 'transform .2s, border-color .2s',
                      '&:hover': c.route ? { transform: 'translateY(-2px)', borderColor: col } : {},
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: col }}>
                        <Box sx={{ display: 'flex', '& svg': { fontSize: 18 } }}>{meta.icon}</Box>
                        <Chip size="small" label={meta.label} sx={{ height: 18, fontSize: 10, fontWeight: 700, color: col, bgcolor: `${col}22`, border: `1px solid ${col}` }} />
                      </Stack>
                      <Typography sx={{ fontSize: 10, color: C.sub, fontFamily: 'monospace' }}>{c.time}</Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.text, mt: 0.75 }} noWrap>{c.title}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: C.sub }} noWrap>{c.detail}</Typography>
                    {c.amount != null && c.amount > 0 && (
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: col, mt: 0.5, fontFamily: 'monospace' }}>
                        {formatIndianNumber(Number(c.amount))}
                      </Typography>
                    )}
                  </Box>
                  {idx < chapters.length - 1 && (
                    <Stack alignItems="center" justifyContent="center" sx={{ px: 0.25, color: C.line, alignSelf: 'center' }}>
                      <East sx={{ fontSize: 16 }} />
                    </Stack>
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        )}
      </Collapse>
    </Box>
  );
};

export default DemoStory;
